export interface ButtonLoadingHTMLElement extends HTMLElement {
    btnLoadingInitialClassWait: boolean,
    btnLoadingInitialClassHidden: boolean,
}

/**
 * Toggle the class "wait"
 * on button with class "btn-loading"
 * 
 * Usage:
 * Add the class "btn-loading" to your button
 * Add the attribute "data-loading-handler" with value:
 * - "click" to define the class "wait" on click (default)
 * - "submit" to define the class "wait" on submitting the parent form
 * - "time(xx)" to define a time is ms before adding the class "wait"
 * - or another javascript event name to define the classe "wait" when the event is triggered
 * Add the attribute "data-loading-hidden" with value "true" to hide the button when loading
 * Add the attribute "data-loading-additional-buttons" with selectors of additional buttons that inherit the behavior separated by a "|"
 * 
 * Use the BtnLoadingResetEvent to reset the state of the button
 * $button.dispatchEvent(BtnLoadingResetEvent);
 * 
 * @param {Document|HTMLElement} $container
 * 
 * @return {void}
 */
export default function initBtnLoading($container: Document|HTMLElement = document) : void {
    $container.querySelectorAll<ButtonLoadingHTMLElement>('.btn-loading').forEach($btn => {
        const handler = $btn.dataset.loadingHandler || 'click';
        const hideWhenLoading = $btn.dataset.loadingHidden === 'true';
        const additionalBtnsSelectors = ($btn.dataset.loadingAdditionalButtons || '').split('|').filter(n => n);
        const $additionalBtns: ButtonLoadingHTMLElement[] = [];

        // Save the initial classes of the button
        $btn.btnLoadingInitialClassWait = $btn.classList.contains('wait');
        $btn.btnLoadingInitialClassHidden = $btn.classList.contains('hidden');

        additionalBtnsSelectors.forEach(selector => {
            const $additionalBtnFounded = $container.querySelectorAll<ButtonLoadingHTMLElement>(selector) || [];

            $additionalBtnFounded.forEach($additionalBtn => {
                if ($additionalBtn !== $btn) {
                    // Save the initial classes of the button
                    $additionalBtn.btnLoadingInitialClassWait = $additionalBtn.classList.contains('wait');
                    $additionalBtn.btnLoadingInitialClassHidden = $additionalBtn.classList.contains('hidden');
                    $additionalBtns.push($additionalBtn);
                }
            });
        });

        if ('submit' === handler) { // Toggle the class when submitting the parent form
            const $form = $btn.closest('form');
            if ($form) {
                $form.addEventListener('submit', () => {
                    toggleClasses($btn, hideWhenLoading);
                    $additionalBtns.forEach($additionalBtn => toggleClasses($additionalBtn, hideWhenLoading));
                });
            }
        } else if (handler.match(/^time\(([0-9]+)\)$/)) { // Toggle the class after XXms
            const result = handler.match(/^time\(([0-9]+)\)$/);
            const timeInMs = parseFloat(result ? result[1] : '0');

            setTimeout(() => {
                toggleClasses($btn, hideWhenLoading);
                $additionalBtns.forEach($additionalBtn => toggleClasses($additionalBtn, hideWhenLoading));
            }, timeInMs);
        } else if ('none' !== handler) {
            $btn.addEventListener(handler, () => {
                if (!$btn.classList.contains('wait')) {
                    toggleClasses($btn, hideWhenLoading);
                    $additionalBtns.forEach($additionalBtn => toggleClasses($additionalBtn, hideWhenLoading));
                }
            });
        }

        $btn.addEventListener('btn-loading-wait-event', () => {
            toggleClasses($btn, hideWhenLoading);
        });

        $additionalBtns.forEach($additionalBtn => {
            $additionalBtn.addEventListener('btn-loading-wait-event', () => {
                toggleClasses($additionalBtn, hideWhenLoading);
            });
        });

        $btn.addEventListener('btn-loading-reset-event', () => {
            resetClasses($btn);
        });

        $additionalBtns.forEach($additionalBtn => {
            $additionalBtn.addEventListener('btn-loading-reset-event', () => {
                resetClasses($additionalBtn);
            });
        });
    });
}

// Event used to reset the state of the button
export const BtnLoadingResetEvent = new CustomEvent('btn-loading-reset-event');

// Event used to set the loading state of the button
export const BtnLoadingWaitEvent = new CustomEvent('btn-loading-wait-event');

/**
 * Toggle classes of the button
 * 
 * @param {ButtonLoadingHTMLElement} $btn 
 * @param {boolean} hideWhenLoading 
 * 
 * @return {void}
 */
function toggleClasses($btn: ButtonLoadingHTMLElement, hideWhenLoading: boolean) : void {
    $btn.classList.toggle('wait');

    if (hideWhenLoading) {
        $btn.classList.toggle('hidden');
    }
}

/**
 * Reset classes of the button
 * 
 * @param {ButtonLoadingHTMLElement} $btn 
 * 
 * @return {void}
 */
function resetClasses($btn: ButtonLoadingHTMLElement) : void {
    if ($btn.btnLoadingInitialClassWait) {
        $btn.classList.add('wait');
    } else {
        $btn.classList.remove('wait');
    }

    if ($btn.btnLoadingInitialClassHidden) {
        $btn.classList.add('hidden');
    } else {
        $btn.classList.remove('hidden');
    }
}
